/**
 * Beta Keys Management Panel
 *
 * Generate, list, copy, and revoke beta invitation keys.
 * Used inside AdminPanel as a tab.
 */

import { useState, useEffect, useCallback } from 'react';
import { trpc } from '../../lib/trpc';
import { Icon } from '../Icon';

interface BetaKey {
  id: string;
  keyCode: string;
  label: string | null;
  createdAt: string;
  claimedAt: string | null;
  claimedBy: string | null;
  expiresAt: string | null;
  isActive: boolean;
}

type KeyStatus = 'available' | 'claimed' | 'revoked' | 'expired';

function getKeyStatus(key: BetaKey): KeyStatus {
  if (!key.isActive) return 'revoked';
  if (key.claimedBy) return 'claimed';
  if (key.expiresAt && new Date(key.expiresAt) < new Date()) return 'expired';
  return 'available';
}

const STATUS_COLORS: Record<KeyStatus, string> = {
  available: '#2ecc71',
  claimed: '#5b9bd5',
  revoked: '#e74c3c',
  expired: '#e67e22',
};

const STATUS_LABELS: Record<KeyStatus, string> = {
  available: 'Available',
  claimed: 'Claimed',
  revoked: 'Revoked',
  expired: 'Expired',
};

export function BetaKeysPanel() {
  const [keys, setKeys] = useState<BetaKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate form
  const [genCount, setGenCount] = useState(1);
  const [genLabel, setGenLabel] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Revoke state
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Copied key feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Bulk generation popup
  const [generatedKeys, setGeneratedKeys] = useState<{ keyCode: string; label: string | null }[] | null>(null);
  const [bulkCopied, setBulkCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await trpc.betaKeys.list.query();
      setKeys(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  async function handleGenerate() {
    setIsGenerating(true);
    setError(null);
    try {
      const result = await trpc.betaKeys.generate.mutate({
        count: genCount,
        label: genLabel.trim() || undefined,
      });
      setGeneratedKeys(result);
      setBulkCopied(false);
      setGenLabel('');
      setGenCount(1);
      await loadKeys();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  }

  function handleCopyAllGenerated() {
    if (!generatedKeys) return;
    const text = generatedKeys.map((k) => k.keyCode).join('\n');
    navigator.clipboard.writeText(text);
    setBulkCopied(true);
  }

  async function handleRevoke(id: string) {
    setRevokingId(id);
    setError(null);
    try {
      await trpc.betaKeys.revoke.mutate({ id });
      await loadKeys();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRevokingId(null);
    }
  }

  function handleCopy(key: BetaKey) {
    navigator.clipboard.writeText(key.keyCode);
    setCopiedId(key.id);
    setTimeout(() => setCopiedId(null), 1500);
  }

  // Stats
  const total = keys.length;
  const claimed = keys.filter((k) => getKeyStatus(k) === 'claimed').length;
  const available = keys.filter((k) => getKeyStatus(k) === 'available').length;
  const revoked = keys.filter((k) => getKeyStatus(k) === 'revoked').length;

  return (
    <div>
      {error && (
        <div style={s.error}>
          <Icon name="alert" size={16} />
          {error}
        </div>
      )}

      {/* Stats */}
      <div style={s.stats}>
        <div style={s.stat}>
          <span style={s.statNumber}>{total}</span>
          <span style={s.statLabel}>Total</span>
        </div>
        <div style={s.stat}>
          <span style={{ ...s.statNumber, color: '#2ecc71' }}>{available}</span>
          <span style={s.statLabel}>Available</span>
        </div>
        <div style={s.stat}>
          <span style={{ ...s.statNumber, color: '#5b9bd5' }}>{claimed}</span>
          <span style={s.statLabel}>Claimed</span>
        </div>
        <div style={s.stat}>
          <span style={{ ...s.statNumber, color: '#e74c3c' }}>{revoked}</span>
          <span style={s.statLabel}>Revoked</span>
        </div>
      </div>

      {/* Generate form */}
      <div style={s.generateForm}>
        <div style={s.formRow}>
          <label style={s.formLabel}>
            Count
            <input
              type="number"
              min={1}
              max={20}
              value={genCount}
              onChange={(e) => setGenCount(Math.max(1, Math.min(20, Number(e.target.value))))}
              style={s.numberInput}
            />
          </label>
          <label style={s.formLabel}>
            Label
            <input
              type="text"
              placeholder="e.g. Player name"
              maxLength={100}
              value={genLabel}
              onChange={(e) => setGenLabel(e.target.value)}
              style={s.textInput}
            />
          </label>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              ...s.generateButton,
              opacity: isGenerating ? 0.6 : 1,
            }}
          >
            {isGenerating ? (
              <Icon name="loader" size={14} />
            ) : (
              <Icon name="plus" size={14} />
            )}
            {isGenerating ? 'Generating...' : `Generate ${genCount > 1 ? `${genCount} Keys` : 'Key'}`}
          </button>
        </div>
      </div>

      {/* Keys table */}
      {isLoading ? (
        <div style={s.loading}>
          <Icon name="loader" size={24} />
          Loading keys...
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Key Code</th>
                <th style={s.th}>Label</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => {
                const status = getKeyStatus(key);
                return (
                  <tr key={key.id} style={s.tr}>
                    <td style={s.tdCode}>
                      <span style={s.keyCode}>{key.keyCode}</span>
                      <button
                        onClick={() => handleCopy(key)}
                        style={s.copyButton}
                        title="Copy to clipboard"
                      >
                        <Icon
                          name={copiedId === key.id ? 'check' : 'copy'}
                          size={12}
                          color={copiedId === key.id ? '#2ecc71' : undefined}
                        />
                      </button>
                    </td>
                    <td style={s.td}>{key.label || <span style={s.muted}>--</span>}</td>
                    <td style={s.td}>
                      <span style={{
                        ...s.badge,
                        backgroundColor: `${STATUS_COLORS[status]}22`,
                        color: STATUS_COLORS[status],
                        borderColor: `${STATUS_COLORS[status]}44`,
                      }}>
                        {STATUS_LABELS[status]}
                      </span>
                    </td>
                    <td style={s.tdDate}>
                      {new Date(key.createdAt).toLocaleDateString()}
                    </td>
                    <td style={s.td}>
                      {status === 'available' && (
                        <button
                          onClick={() => handleRevoke(key.id)}
                          disabled={revokingId === key.id}
                          style={s.revokeButton}
                          title="Revoke key"
                        >
                          {revokingId === key.id ? (
                            <Icon name="loader" size={12} />
                          ) : (
                            <Icon name="close" size={12} />
                          )}
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {keys.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...s.td, textAlign: 'center', padding: '24px' }}>
                    <span style={s.muted}>No beta keys yet. Generate some above.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Refresh */}
      <button onClick={loadKeys} style={s.refreshButton}>
        <Icon name="refresh-cw" size={14} />
        Refresh
      </button>

      {/* Generated keys popup */}
      {generatedKeys && (
        <div style={s.overlay} onClick={() => setGeneratedKeys(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span>{generatedKeys.length === 1 ? 'Key Generated' : `${generatedKeys.length} Keys Generated`}</span>
              <button onClick={() => setGeneratedKeys(null)} style={s.modalClose}>
                <Icon name="close" size={16} />
              </button>
            </div>
            <div style={s.modalBody}>
              {generatedKeys.map((k, i) => (
                <div key={i} style={s.modalKeyRow}>
                  <span style={s.keyCode}>{k.keyCode}</span>
                  {k.label && <span style={s.modalLabel}>{k.label}</span>}
                </div>
              ))}
            </div>
            <button onClick={handleCopyAllGenerated} style={s.copyAllButton}>
              <Icon name={bulkCopied ? 'check' : 'copy'} size={14} color={bulkCopied ? '#2ecc71' : '#1a1410'} />
              {bulkCopied ? 'Copied!' : generatedKeys.length === 1 ? 'Copy Key' : 'Copy All Keys'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const GOLD = '#d4af37';

const s: Record<string, React.CSSProperties> = {
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-danger)',
  },

  stats: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)',
    justifyContent: 'center',
  },

  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    minWidth: 80,
  },

  statNumber: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 700,
    color: GOLD,
  },

  statLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  generateForm: {
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)',
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },

  formRow: {
    display: 'flex',
    gap: 'var(--spacing-md)',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },

  formLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  numberInput: {
    width: 60,
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  textInput: {
    flex: 1,
    minWidth: 160,
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
  },

  generateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: '6px 14px',
    backgroundColor: GOLD,
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-xl)',
    color: 'var(--color-text-secondary)',
  },

  tableWrap: {
    overflowX: 'auto',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--font-size-sm)',
  },

  th: {
    textAlign: 'left',
    padding: '8px 12px',
    backgroundColor: 'var(--color-background-elevated)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--color-border)',
    whiteSpace: 'nowrap',
  },

  tr: {
    borderBottom: '1px solid var(--color-border)',
  },

  td: {
    padding: '8px 12px',
    color: 'var(--color-text)',
    verticalAlign: 'middle',
  },

  tdCode: {
    padding: '8px 12px',
    color: 'var(--color-text)',
    verticalAlign: 'middle',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  tdDate: {
    padding: '8px 12px',
    color: 'var(--color-text-secondary)',
    verticalAlign: 'middle',
    fontSize: 'var(--font-size-xs)',
    whiteSpace: 'nowrap',
  },

  keyCode: {
    fontFamily: 'monospace',
    fontSize: 'var(--font-size-sm)',
    letterSpacing: '0.03em',
  },

  copyButton: {
    padding: 4,
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
  },

  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    border: '1px solid',
    whiteSpace: 'nowrap',
  },

  revokeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-danger)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  muted: {
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    marginTop: 'var(--spacing-lg)',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },

  modal: {
    backgroundColor: '#1a1410',
    border: `1px solid ${GOLD}`,
    borderRadius: 'var(--radius-md)',
    padding: 0,
    minWidth: 340,
    maxWidth: 480,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },

  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
    color: GOLD,
    fontWeight: 600,
    fontSize: 'var(--font-size-md)',
  },

  modalClose: {
    padding: 4,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
  },

  modalBody: {
    padding: '12px 16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  modalKeyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
  },

  modalLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    marginLeft: 12,
  },

  copyAllButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-xs)',
    margin: '12px 16px 16px',
    padding: '8px 16px',
    backgroundColor: GOLD,
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
